import { useState } from 'react';
import { SignaturePad } from '../components/SignaturePad.jsx';
import { useStore } from '../store.js';
import { api } from '../api.js';

function uuid() {
  return crypto.randomUUID();
}

export function Sign({ onSigned, onBack }) {
  const { token, owners } = useStore((s) => ({ token: s.token, owners: s.owners }));
  const [primaryOwnerId, setPrimaryOwnerId] = useState(owners[0]?.id || '');
  const [signature, setSignature] = useState(null);
  const [consentEsign, setConsentEsign] = useState(false);
  const [consentCredit, setConsentCredit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSign = !!signature && consentEsign && consentCredit && primaryOwnerId;

  async function submit() {
    setError(null);
    if (!canSign) return;
    setBusy(true);
    try {
      const resp = await api.sign(token, {
        signature_svg: signature.svg,
        signature_png_b64: signature.png_b64,
        consent_esign: true,
        consent_credit: true,
        idempotency_key: uuid(),
        primary_owner_id: primaryOwnerId,
      });
      onSigned?.(resp?.status);
    } catch (e) {
      setError(e.body?.message || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-xl font-semibold text-brand-900">Sign your application</h2>
        <p className="text-sm text-slate-500">Your signature, the date/time, and your IP address will be recorded.</p>
      </header>

      <div className="card p-4 space-y-3">
        <label className="field-label">Signing as</label>
        <select className="field-select" value={primaryOwnerId} onChange={(e) => setPrimaryOwnerId(e.target.value)}>
          {owners.map((o) => (
            <option key={o.id} value={o.id}>{o.first_name} {o.last_name} — {o.ownership_pct}%</option>
          ))}
        </select>
      </div>

      <div className="card p-4">
        <SignaturePad onChange={setSignature} />
      </div>

      <div className="card p-4 space-y-3 text-sm">
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-1" checked={consentEsign} onChange={(e) => setConsentEsign(e.target.checked)} />
          <span>I agree to sign this application electronically under the E-SIGN Act, and I consent to receive related disclosures electronically.</span>
        </label>
        <label className="flex items-start gap-3">
          <input type="checkbox" className="mt-1" checked={consentCredit} onChange={(e) => setConsentCredit(e.target.checked)} />
          <span>I authorize Fundmerica and its funding partners to obtain consumer and business credit reports for the purpose of evaluating this application.</span>
        </label>
      </div>

      {error && <div className="field-error">{error}</div>}

      <div className="flex justify-between">
        <button className="btn-ghost" onClick={onBack} disabled={busy}>Back</button>
        <button className="btn-primary" onClick={submit} disabled={!canSign || busy}>
          {busy ? 'Submitting…' : 'Sign and submit'}
        </button>
      </div>
    </div>
  );
}
